import './AddTray.scss'
import {
  Alert,
  Snackbar,
  IconButton,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Breadcrumbs,
  Link,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import Stepper from '@mui/material/Stepper'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import { useTrayStepContext } from './AddTray.logic'
import { useAddTrayContext } from './AddTray.context'
import { useState, useEffect, useRef } from 'react'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import { ArrowBack } from '@mui/icons-material'
import { useLocation } from 'react-router-dom'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import GuidancePanel from './GuidancePanel/GuidancePanel'
import useSubTraysLogic from './SubTrays/SubTrays.logic'
import { useIsTablet } from 'lib/utils/mediaQueries'
import step1Image from './step1_trayID.png'
import step2Image from './step2_TrayDetails.png'
import step3Image from './step3_ParentTrayImage.png'
import step4_1 from './step4_AddTrays-1.png'
import step4_2 from './step4_AddTrays-2.png'
import step4_3 from './step4_AddTrays-3.png'
import step5Image from './step5_SupportingDocuments.png'
import step6Image from './step6_Assign.png'
import step7Image from './step7_TrayList.png'
import step8Image from './step8_Review.png'
import { useMediaQuery } from '@mui/material'

const AddTray = () => {
  const location = useLocation()
  const isTablet = useIsTablet()
  // Use different layouts for different screen sizes
  const isMobile = useMediaQuery('(max-width:600px)')
  const isTabletPortrait = useMediaQuery(
    '(max-width:768px) and (min-width:601px)'
  )
  const shouldUseVerticalStepper = isMobile // Only mobile gets vertical
  const shouldUseTabletStepper = isTabletPortrait // Tablet gets special horizontal scrollable

  // Add ref for smart scrolling
  const stepperRef = useRef<HTMLDivElement>(null)

  const {
    trays,
    snackbarOpen,
    snackbarIcon,
    snackbarMessage,
    snackbarState,
    handleSnackbarClose,
    currentTrayIndex,
    loadTrayItem,
    loadRejectedTrayData,
  } = useAddTrayContext()
  const {
    stepNumber,
    steps,
    currentStep,
    goToStep,
    highestStepReached,
    handleDiscardAndExit,
  } = useTrayStepContext()
  const { currentView } = useSubTraysLogic()

  const [showBackConfirmation, setShowBackConfirmation] = useState(false)
  const [isFixingRejectedTray, setIsFixingRejectedTray] = useState(false)
  const [showGuidance, setShowGuidance] = useState(false)
  const [step4ImageIndex, setStep4ImageIndex] = useState(0)

  const toggleGuidance = () => {
    setShowGuidance(!showGuidance)
  }

  // Parse URL parameters to check for rejected tray data
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const rejectedTrayData = searchParams.get('rejectedTrayData')

    if (rejectedTrayData) {
      try {
        const decodedData = JSON.parse(
          Buffer.from(rejectedTrayData, 'base64').toString()
        )
        if (decodedData.isRejectedTrayFix && decodedData.trayItem) {
          loadRejectedTrayData(decodedData.trayItem)
          setIsFixingRejectedTray(true)
        }
      } catch (error) {
        console.error('Error parsing rejected tray data:', error)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search])

  useEffect(() => {
    if (currentStep.label === 'Add Trays' || currentStep.label === 'Add Tray') {
      switch (currentView) {
        case 'list':
          setStep4ImageIndex(0)
          break
        case 'analyze':
          setStep4ImageIndex(1)
          break
        default:
          setStep4ImageIndex(0)
      }
    }
  }, [currentStep, currentView])

  // Smart scrolling for tablet horizontal stepper
  useEffect(() => {
    if (shouldUseTabletStepper && stepperRef.current) {
      const activeStepElement = stepperRef.current.querySelector(
        `[data-step="${stepNumber}"]`
      )
      if (activeStepElement) {
        activeStepElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        })
      }

      // Update progress bar
      const progressPercentage = ((stepNumber + 1) / steps.length) * 100
      stepperRef.current.style.setProperty(
        '--progress-percentage',
        `${progressPercentage}%`
      )
    }
  }, [stepNumber, shouldUseTabletStepper, steps.length])

  // Function to handle returning to the tray list
  const handleBackToList = () => {
    setShowBackConfirmation(true)
  }

  const handleConfirmBackToList = () => {
    // If we're editing, reload the original tray data to discard changes
    if (currentTrayIndex >= 0) {
      loadTrayItem(currentTrayIndex)
    }

    // Go to the tray list step (index 6 based on our steps array)
    goToStep(6)
    setShowBackConfirmation(false)
  }

  const handleCancelBackToList = () => {
    setShowBackConfirmation(false)
  }

  const getGuidanceImages = (stepLabel: string) => {
    if (stepLabel === 'Add Trays' || stepLabel === 'Add Tray') {
      const step4Images = [step4_1, step4_2, step4_3]
      return [step4Images[step4ImageIndex]]
    }

    const imageMap: Record<string, string> = {
      'Tray ID': step1Image,
      'Tray Details': step2Image,
      'Parent Tray Image': step3Image,
      'Supporting Documents': step5Image,
      Assign: step6Image,
      'Tray List': step7Image,
      Review: step8Image,
    }

    return [imageMap[stepLabel] || step1Image]
  }

  return (
    <>
      <div className="step-layout">
        <div className="step-layout_header">
          <IconButton
            size="large"
            color="primary"
            onClick={handleDiscardAndExit}
            className="discard-exit-button"
          >
            <CloseIcon />
          </IconButton>

          {/* Add breadcrumb navigation */}
          {(currentTrayIndex >= 0 || isFixingRejectedTray) &&
            stepNumber < 3 && (
              <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small" />}
                aria-label="breadcrumb"
                className="tray-breadcrumbs"
              >
                <Link
                  color="inherit"
                  onClick={handleBackToList}
                  style={{ cursor: 'pointer' }}
                >
                  {isFixingRejectedTray ? 'Fix Rejected Tray' : 'Tray List'}
                </Link>
                <Typography color="textPrimary">
                  {isFixingRejectedTray
                    ? 'Description'
                    : `Edit Tray: ${
                        trays[currentTrayIndex]?.trayDescription || 'Tray'
                      }`}
                </Typography>
                {currentTrayIndex >= 0 &&
                  !isFixingRejectedTray &&
                  stepNumber < 3 && (
                    <Button
                      variant="outlined"
                      startIcon={<ArrowBack />}
                      onClick={handleBackToList}
                      className="back-to-list-button"
                      size="small"
                    >
                      Back to Tray List
                    </Button>
                  )}
              </Breadcrumbs>
            )}

          <Stepper
            activeStep={stepNumber}
            className={`stepper ${
              shouldUseVerticalStepper ? 'stepper--mobile' : ''
            } ${shouldUseTabletStepper ? 'stepper--tablet-horizontal' : ''} ${
              isTablet ? 'stepper--tablet' : ''
            }`}
            orientation={shouldUseVerticalStepper ? 'vertical' : 'horizontal'}
            ref={stepperRef}
          >
            {steps.map((step, index) => (
              <Step
                key={step.label}
                completed={index < stepNumber}
                onClick={() => goToStep(index)}
                disabled={index > highestStepReached}
                data-step={index}
              >
                <StepLabel
                  classes={{
                    root:
                      index > highestStepReached
                        ? 'step-root--disabled'
                        : 'step-root',
                    label:
                      index > highestStepReached
                        ? 'step-label--disabled'
                        : 'step-label',
                    labelContainer:
                      index > highestStepReached
                        ? 'step-label-container--disabled'
                        : 'step-label-container',
                  }}
                >
                  {step.label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </div>
        <div className="step-view">
          <div className="step-container">
            {isFixingRejectedTray && (
              <div className="step-guidance-banner fixing-rejected-tray">
                <Typography variant="body1">
                  <strong>Fixing Rejected Tray:</strong> You are currently
                  fixing a rejected tray. Make the necessary corrections and
                  submit to reprocess the tray.
                </Typography>
              </div>
            )}
            {currentStep.guidance && (
              <div className="step-guidance-banner">
                <Typography variant="body1">
                  <strong>
                    {currentStep.customGuideLabel || currentStep.label}:
                  </strong>{' '}
                  {currentStep.guidance}
                </Typography>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={toggleGuidance}
                  startIcon={
                    showGuidance ? <VisibilityOffIcon /> : <HelpOutlineIcon />
                  }
                  size="small"
                  className="guidance-toggle-button"
                >
                  {showGuidance ? 'Hide Guide' : 'Show Guide'}
                </Button>
              </div>
            )}

            {showGuidance && currentStep.guidance && (
              <GuidancePanel
                title={`${
                  currentStep.customGuideLabel || currentStep.label
                } Instructions`}
                images={getGuidanceImages(currentStep.label)}
              />
            )}

            <currentStep.Component />
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog
        open={showBackConfirmation}
        onClose={handleCancelBackToList}
        aria-labelledby="back-to-list-confirmation"
      >
        <DialogTitle id="back-to-list-confirmation">
          Return to Tray List?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Any unsaved changes to this tray will be lost. Are you sure you want
            to return to the tray list?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelBackToList} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmBackToList} color="primary" autoFocus>
            Return to List
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarState}
          sx={{ width: '100%' }}
          icon={snackbarIcon}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  )
}

export default AddTray
